/**
 * Utility functions for keyboard shortcuts and OS detection
 */

/**
 * Detect if the user is on a Mac
 */
export const isMac = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform) || /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
};

/**
 * Get the modifier key symbol based on OS
 * Returns ⌘ for Mac, Ctrl for Windows/Linux
 */
export const getModifierKey = (): string => {
  return isMac() ? '⌘' : 'Ctrl';
};

/**
 * Get the modifier key name for display
 * Returns 'Cmd' for Mac, 'Ctrl' for Windows/Linux
 */
export const getModifierKeyName = (): string => {
  return isMac() ? 'Cmd' : 'Ctrl';
};

/**
 * Format a keyboard shortcut for display
 * @param keys - Array of keys in the shortcut (e.g., ['ctrl', 'S'] or ['cmd', 'shift', 'P'])
 * @returns Formatted shortcut string (e.g., 'Ctrl+S' or '⌘⇧P')
 */
export const formatShortcut = (keys: string[]): string => {
  const modifierKey = getModifierKey();
  
  return keys
    .map(key => {
      const lowerKey = key.toLowerCase();
      
      // Replace modifier keys
      if (lowerKey === 'ctrl' || lowerKey === 'cmd' || lowerKey === 'meta') {
        return modifierKey;
      }
      if (lowerKey === 'shift') {
        return isMac() ? '⇧' : 'Shift';
      }
      if (lowerKey === 'alt' || lowerKey === 'option') {
        return isMac() ? '⌥' : 'Alt';
      }
      if (lowerKey === 'backspace' || lowerKey === 'delete') {
        return isMac() ? '⌫' : 'Del';
      }
      if (lowerKey === 'enter' || lowerKey === 'return') {
        return isMac() ? '↵' : 'Enter';
      }
      if (lowerKey === 'escape' || lowerKey === 'esc') {
        return 'Esc';
      }
      
      // Return the key as-is (uppercase for single letters)
      return key.length === 1 ? key.toUpperCase() : key;
    })
    .join(isMac() ? '' : '+');
};

/**
 * Get common keyboard shortcuts formatted for the current OS
 * This is a function to ensure OS detection happens at runtime
 */
export const getShortcuts = () => ({
  copy: formatShortcut(['ctrl', 'C']),
  cut: formatShortcut(['ctrl', 'X']),
  paste: formatShortcut(['ctrl', 'V']),
  duplicate: formatShortcut(['ctrl', 'D']),
  delete: formatShortcut(['delete']),
  selectAll: formatShortcut(['ctrl', 'A']),
  save: formatShortcut(['ctrl', 'S']),
  undo: formatShortcut(['ctrl', 'Z']),
  redo: formatShortcut(['ctrl', 'Shift', 'Z']),
  find: formatShortcut(['ctrl', 'F']),
  execute: formatShortcut(['ctrl', 'Enter']),
  zoomIn: formatShortcut(['ctrl', '+']),
  zoomOut: formatShortcut(['ctrl', '-']),
  fitView: formatShortcut(['ctrl', '0']),
  escape: 'Esc',
});

/**
 * Common keyboard shortcuts formatted for the current OS
 * @deprecated Use getShortcuts() instead for proper OS detection
 */
export const shortcuts = getShortcuts();
